/**
 * 보고서 PDF 의 HTML. 앱은 이것을 인쇄 창에 쓰기만 한다 — 여기 두어야 시험할 수 있다.
 *
 * 예전 틀은 「AI 가 만든 문서」의 기본값 그대로였다 — 넓은 자간의 영문 머리말, 왼쪽 색 띠
 * 카드, 남색 표 머리, 고정 문구 요약(2026-10-23). 경영 보고(IBCS)의 순서를 따른다:
 * 결론이 제목, 그래프는 막대 끝에 숫자, 장식 없는 표. 스킬의 스타일은 쓰지 않는다 —
 * 그것이 바로 색 띠 카드를 되살린다.
 */
import type { ReportChart, ReportDocument, TableData } from './files';
import { withThousands } from './korean';
import { shortDate } from './reportData';

/** 색각 검사(dataviz validate_palette, light)를 통과한 두 색. 강조 계열만 주황이다. */
const BASE = '#2a78d6';
const ATTENTION = '#eb6834';
const INK = '#0b0b0b';
const SECONDARY = '#52514e';
const MUTED = '#898781';
const HAIRLINE = '#e1e0d9';

const WIDTH = 660;
const LABEL = 150;
const VALUE = 70;
const PLOT = WIDTH - LABEL - VALUE;
const BAR = 12;
const GAP = 2;
const BAND_PAD = 6;
/** 이름 칸에 들어가는 글자 수. 넘으면 말줄임. 「가나다라마바사아자 1단지」(열세 글자)가 칸 끝에 닿았다. */
const LABEL_CHARS = 12;
const RADIUS = 4;

function escape(value: unknown): string {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // 작은따옴표는 그대로 — 기준일 「'26. 10. 23.」이 &#39;26 으로 찍힌다. 속성은 큰따옴표로만 감싼다.
    .replace(/"/g, '&quot;');
}

/** 그래프 값은 숫자다 — 네 자리도 쉼표를 찍는다. `withThousands` 는 연도로 읽힐까 네 자리를 건너뛴다. */
function shown(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * 그래프 위 숫자. 큰 금액은 억·만으로 — 「530,000,000」은 막대 끝에서 읽히지 않는다.
 * 한 그래프 안에서는 같은 단위로 적는다(가장 큰 값이 단위를 정한다). 건수처럼 작은 값은 그대로.
 */
function chartNumber(value: number, max: number): string {
  if (max >= 100000000) return `${(Math.round(value / 10000000) / 10).toLocaleString('en-US')}억`;
  if (max >= 1000000) return `${Math.round(value / 10000).toLocaleString('en-US')}만`;
  return shown(value);
}

/** 바닥은 네모, 끝만 둥근 막대. */
function barPath(x: number, y: number, length: number): string {
  if (length <= RADIUS) return `M${x},${y}h${length}v${BAR}h${-length}z`;
  return `M${x},${y}h${length - RADIUS}a${RADIUS},${RADIUS} 0 0 1 ${RADIUS},${RADIUS}`
    + `v${BAR - 2 * RADIUS}a${RADIUS},${RADIUS} 0 0 1 ${-RADIUS},${RADIUS}h${-(length - RADIUS)}z`;
}

function fitted(label: string): string {
  return label.length > LABEL_CHARS ? `${label.slice(0, LABEL_CHARS - 1)}…` : label;
}

const LEGEND = 22;
const FONT = '"맑은 고딕","Malgun Gothic",sans-serif';

/** 계열 색 차례(dataviz 기준 팔레트, 색각 검사 통과). 주황은 강조 계열 몫이다. */
const PALETTE = [BASE, ATTENTION, '#1baf7a', '#eda100', '#e87ba4', '#6250d6'];

/**
 * 강조 계열이 있으면 그것만 주황, 나머지는 파랑부터 주황을 건너뛴 차례로.
 * 없으면 계열마다 차례대로 — 매출·매입이 둘 다 파랑이라 가려 볼 수 없었다(2026-10-23).
 */
export function seriesColor(chart: ReportChart, name: string): string {
  return colorOf(chart, name);
}

function colorOf(chart: ReportChart, name: string): string {
  if (name === chart.highlight) return ATTENTION;
  const others = chart.series.map((series) => series.name).filter((one) => one !== chart.highlight);
  const palette = chart.highlight ? PALETTE.filter((color) => color !== ATTENTION) : PALETTE;
  return palette[Math.max(0, others.indexOf(name)) % palette.length] as string;
}

const LINE_TOP = 18;
const LINE_HEIGHT = 150;
const LINE_BOTTOM = 26;
const LINE_LEFT = 40;
const LINE_RIGHT = 60;
/** 점이 이보다 많으면 끝 값과 가장 큰 값만 적는다 — 점마다 숫자를 달면 읽히지 않는다. */
const LABEL_ALL = 6;

/** 선 그래프. 빈 값에서는 선을 끊는다 — 없는 달을 이어 그리면 있었던 것처럼 읽힌다. */
function lineSvg(chart: ReportChart, options: { standalone?: boolean }): string {
  const values = chart.series.reduce<(number | null)[]>((all, series) => all.concat(series.values), [])
    .filter((value): value is number => value !== null && Number.isFinite(value));
  const max = Math.max(1, ...values);
  const legendHeight = chart.series.length > 1 ? LEGEND : 0;
  const top = legendHeight + LINE_TOP;
  const height = top + LINE_HEIGHT + LINE_BOTTOM;
  const plot = WIDTH - LINE_LEFT - LINE_RIGHT;
  const count = chart.categories.length;
  const xOf = (index: number) => LINE_LEFT + (count > 1 ? (index * plot) / (count - 1) : plot / 2);
  const yOf = (value: number) => top + LINE_HEIGHT * (1 - Math.max(0, value) / max);
  const legend = legendHeight ? chart.series.map((series, order) => {
    const x = LINE_LEFT + order * 110;
    return `<rect x="${x}" y="4" width="10" height="10" rx="2" fill="${colorOf(chart, series.name)}"/>`
      + `<text x="${x + 15}" y="13" fill="${SECONDARY}">${escape(series.name)}</text>`;
  }).join('') : '';
  const marks = chart.series.map((series) => {
    const color = colorOf(chart, series.name);
    const runs: number[][] = [[]];
    series.values.forEach((value, index) => {
      if (value === null || value === undefined) runs.push([]);
      else (runs[runs.length - 1] as number[]).push(index);
    });
    const peak = Math.max(...series.values.filter((value): value is number => value !== null));
    const last = series.values.reduce<number>((found, value, index) => (value === null ? found : index), -1);
    const lines = runs.filter((run) => run.length >= 2).map((run) => (
      `<polyline points="${run.map((index) => `${xOf(index).toFixed(1)},${yOf(series.values[index] as number).toFixed(1)}`).join(' ')}"`
      + ` fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`)).join('');
    const dots = series.values.map((value, index) => {
      if (value === null || value === undefined) return '';
      const x = xOf(index).toFixed(1);
      const y = yOf(value);
      const label = count <= LABEL_ALL || index === last || value === peak
        ? `<text x="${x}" y="${(y - 9).toFixed(1)}" text-anchor="middle" fill="${SECONDARY}">${escape(chartNumber(value, max))}</text>` : '';
      return `<circle cx="${x}" cy="${y.toFixed(1)}" r="4" fill="${color}" stroke="#fff" stroke-width="2"/>${label}`;
    }).join('');
    return lines + dots;
  }).join('');
  const axis = chart.categories.map((category, index) => (
    `<text x="${xOf(index).toFixed(1)}" y="${top + LINE_HEIGHT + 18}" text-anchor="middle" fill="${INK}">${escape(fitted(category))}</text>`)).join('');
  const frame = options.standalone
    ? `xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${height}" font-family='${FONT}' font-size="12"`
    : 'width="100%"';
  return `<svg ${frame} viewBox="0 0 ${WIDTH} ${height}" role="img" aria-label="${escape(chart.title)}">`
    + `${options.standalone ? `<rect width="${WIDTH}" height="${height}" fill="#fff"/>` : ''}${options.standalone ? legend : ''}`
    + `<line x1="${LINE_LEFT}" y1="${top + LINE_HEIGHT}" x2="${WIDTH - LINE_RIGHT + 20}" y2="${top + LINE_HEIGHT}" stroke="${HAIRLINE}"/>`
    + `${marks}${axis}</svg>`;
}

/**
 * 막대 그래프 SVG. `standalone` 이면 워드에 그림으로 넣을 수 있게 혼자 선다 — 페이지 CSS 가 없으니
 * xmlns·글꼴·크기를 속성으로 싣고, 범례도 그림 안에 그린다.
 */
/** 100% 누적 막대 하나 — 칸마다 이름·비율·건수를 그 아래에. */
function shareSvg(chart: ReportChart, options: { standalone?: boolean }): string {
  const values = (chart.series[0]?.values ?? []).map((value) => (value === null ? 0 : value));
  const total = values.reduce((sum, value) => sum + value, 0) || 1;
  const height = 64;
  let x = 0;
  let labelEnd = -Infinity;
  const marks = chart.categories.map((category, index) => {
    const value = values[index] ?? 0;
    const width = (value / total) * WIDTH;
    // 칸을 계열처럼 보고 같은 색 규칙 — 주의 칸만 주황(발표자료와 같다).
    const fill = colorOf({ ...chart, series: chart.categories.map((name) => ({ name, values: [] })) }, category);
    const percent = Math.round((value / total) * 1000) / 10;
    const label = `${category} ${percent}% (${shown(value)})`;
    // 칸이 좁으면 이름이 겹친다 — 앞 이름 끝에서 띄운다.
    const at = Math.max(x, labelEnd + 12);
    labelEnd = at + label.length * 9;
    const mark = `<rect x="${x.toFixed(1)}" y="6" width="${Math.max(0, width - 2).toFixed(1)}" height="26" fill="${fill}"/>`
      + `<text x="${at.toFixed(1)}" y="52" fill="${INK}">${escape(label)}</text>`;
    x += width;
    return mark;
  }).join('');
  const frame = options.standalone
    ? `xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${height}" font-family='${FONT}' font-size="12"`
    : 'width="100%"';
  return `<svg ${frame} viewBox="0 0 ${WIDTH} ${height}" role="img" aria-label="${escape(chart.title)}">`
    + `${options.standalone ? `<rect width="${WIDTH}" height="${height}" fill="#fff"/>` : ''}${marks}</svg>`;
}

/** 누적 막대 — 범주마다 막대 하나에 계열 칸을 이어 그리고 끝에 합계. */
function stackSvg(chart: ReportChart, options: { standalone?: boolean }): string {
  const totals = chart.categories.map((_, index) => chart.series
    .reduce((sum, series) => sum + (series.values[index] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const twoLines = chart.categories.some((category) => wrapped(category).length > 1);
  const band = Math.max(BAR + BAND_PAD, twoLines ? 26 : 0);
  const legendHeight = (options.standalone ? LEGEND : 0) + (twoLines ? 6 : 0);
  const height = chart.categories.length * band + legendHeight;
  const legend = options.standalone ? chart.series.map((series, order) => {
    const x = LABEL + order * 110;
    return `<rect x="${x}" y="4" width="10" height="10" rx="2" fill="${colorOf(chart, series.name)}"/>`
      + `<text x="${x + 15}" y="13" fill="${SECONDARY}">${escape(series.name)}</text>`;
  }).join('') : '';
  const marks = chart.categories.map((category, index) => {
    const y = legendHeight + index * band + BAND_PAD / 2;
    let x = LABEL;
    const parts = chart.series.map((series) => {
      const value = series.values[index] ?? 0;
      const length = (Math.max(0, value) / max) * PLOT;
      const part = length > 0
        ? `<rect x="${x.toFixed(1)}" y="${y}" width="${Math.max(0, length - 1).toFixed(1)}" height="${BAR}" fill="${colorOf(chart, series.name)}"/>`
        : '';
      x += length;
      return part;
    }).join('');
    const lines = wrapped(category);
    const middle = y + BAR / 2 + 4;
    const name = lines.length === 1
      ? escape(lines[0])
      : lines.map((line, at) => `<tspan x="${LABEL - 8}" dy="${at ? 12 : -5}">${escape(line)}</tspan>`).join('');
    return `<text x="${LABEL - 8}" y="${middle}" text-anchor="end" fill="${INK}"><title>${escape(category)}</title>${name}</text>`
      + `${parts}<text x="${(x + 6).toFixed(1)}" y="${y + BAR - 2}" fill="${SECONDARY}">${escape(chartNumber(totals[index] ?? 0, max))}</text>`;
  }).join('');
  const frame = options.standalone
    ? `xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${height}" font-family='${FONT}' font-size="12"`
    : 'width="100%"';
  return `<svg ${frame} viewBox="0 0 ${WIDTH} ${height}" role="img" aria-label="${escape(chart.title)}">`
    + `${options.standalone ? `<rect width="${WIDTH}" height="${height}" fill="#fff"/>` : ''}${legend}`
    + `<line x1="${LABEL}" y1="${legendHeight}" x2="${LABEL}" y2="${height}" stroke="${HAIRLINE}"/>${marks}</svg>`;
}

/** 칸 이름: 길면 자르지 않고 두 줄로(검토, 2026-10-23: 「가나다라마바사아자 1…」이 잘렸다). 두 줄도 넘으면 말줄임. */
const LINE_CHARS = 10;

function wrapped(label: string): string[] {
  if (label.length <= LINE_CHARS) return [label];
  // 띄어쓰기에서 끊고, 없으면 반으로 — 「화정무등파크자치위원 / 회」로 단어 끝에서 끊겼다.
  const space = label.lastIndexOf(' ', LINE_CHARS);
  const cut = space > 3 ? space : label.length <= LINE_CHARS * 2 ? Math.ceil(label.length / 2) : LINE_CHARS;
  const rest = label.slice(cut).trim();
  return [label.slice(0, cut).trim(), rest.length > LINE_CHARS ? `${rest.slice(0, LINE_CHARS - 1)}…` : rest];
}

export function chartSvg(chart: ReportChart, options: { standalone?: boolean } = {}): string {
  if (chart.kind === 'line') return lineSvg(chart, options);
  if (chart.kind === 'share') return shareSvg(chart, options);
  if (chart.kind === 'stack') return stackSvg(chart, options);
  const values = chart.series.reduce<(number | null)[]>((all, series) => all.concat(series.values), [])
    .filter((value): value is number => value !== null && Number.isFinite(value));
  const max = Math.max(1, ...values);
  const twoLines = chart.categories.some((category) => wrapped(category).length > 1);
  const band = Math.max(chart.series.length * (BAR + GAP) - GAP + BAND_PAD, twoLines ? 26 : 0);
  // 두 줄 이름은 윗줄이 막대 위로 올라간다 — 그림 위에 그만큼 띄운다(윗줄이 잘렸다, 2026-10-23).
  // 범례는 계열이 둘 이상일 때만. 띄운 6px를 범례 높이로 보고 계열 하나인 막대 위에 범례를 겹쳐 그렸다(13차 검토 판).
  const legendShown = !!options.standalone && chart.series.length > 1;
  const legendHeight = (legendShown ? LEGEND : 0) + (twoLines ? 6 : 0);
  const height = chart.categories.length * band + legendHeight;
  const legend = legendShown ? chart.series.map((series, order) => {
    const x = LABEL + order * 110;
    return `<rect x="${x}" y="4" width="10" height="10" rx="2" fill="${colorOf(chart, series.name)}"/>`
      + `<text x="${x + 15}" y="13" fill="${SECONDARY}">${escape(series.name)}</text>`;
  }).join('') : '';
  const marks = chart.categories.map((category, index) => {
    const top = legendHeight + index * band + BAND_PAD / 2;
    const bars = chart.series.map((series, order) => {
      const y = top + order * (BAR + GAP);
      const value = series.values[index];
      // 자료가 없는 칸은 막대 없이 「없음」 — 0 막대는 「0이었다」로 읽힌다.
      if (value === null || value === undefined) {
        return `<text x="${LABEL + 6}" y="${y + BAR - 2}" fill="${MUTED}">없음</text>`;
      }
      const share = Math.min(100, Math.max(0, (value / max) * 100));
      const length = (share / 100) * PLOT;
      const fill = colorOf(chart, series.name);
      return `<path d="${barPath(LABEL, y, length)}" fill="${fill}" data-length="${share.toFixed(1)}"/>`
        + `<text x="${LABEL + length + 6}" y="${y + BAR - 2}" fill="${SECONDARY}">${escape(chartNumber(value, max))}</text>`;
    }).join('');
    const middle = top + (chart.series.length * (BAR + GAP) - GAP) / 2 + 4;
    const lines = wrapped(category);
    const name = lines.length === 1
      ? escape(lines[0])
      : lines.map((line, at) => `<tspan x="${LABEL - 8}" dy="${at ? 12 : -5}">${escape(line)}</tspan>`).join('');
    return `<text x="${LABEL - 8}" y="${middle}" text-anchor="end" fill="${INK}"><title>${escape(category)}</title>${name}</text>${bars}`;
  }).join('');
  const frame = options.standalone
    ? `xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${height}" font-family='${FONT}' font-size="12"`
    : 'width="100%"';
  return `<svg ${frame} viewBox="0 0 ${WIDTH} ${height}" role="img" aria-label="${escape(chart.title)}">`
    + `${options.standalone ? `<rect width="${WIDTH}" height="${height}" fill="#fff"/>` : ''}${legend}`
    + `<line x1="${LABEL}" y1="${legendHeight}" x2="${LABEL}" y2="${height}" stroke="${HAIRLINE}"/>${marks}</svg>`;
}

function chartSection(chart: ReportChart): string {
  const legend = chart.series.length > 1
    ? `<div class="legend">${chart.series.map((series) => (
      `<span><i style="background:${colorOf(chart, series.name)}"></i>${escape(series.name)}</span>`
    )).join('')}</div>`
    : '';
  // 제목은 결론 문장, 그래프 이름은 그 아래 작은 설명으로.
  const heading = chart.message
    ? `<h2>${escape(chart.message)}</h2><p class="caption">${escape(chart.title)}</p>`
    : `<h2>${escape(chart.title)}</h2>`;
  return `<section class="chart">${heading}${legend}${chartSvg(chart)}</section>`;
}

function numeric(column: number, rows: unknown[][]): boolean {
  const cells = rows.map((row) => row[column]).filter((cell) => cell !== null && cell !== undefined && cell !== '');
  return cells.length > 0 && cells.every((cell) => typeof cell === 'number' || /^-?[\d,]+(?:\.\d+)?$/.test(String(cell).trim()));
}

/** 표 칸의 글자. 수는 네 자리도 쉼표, 날짜(20261001)는 '26.10.1. 로. */
export function cellText(value: unknown): string {
  if (typeof value === 'number') return shown(value);
  const text = String(value ?? '');
  const dated = shortDate(text);
  return dated !== text ? dated : withThousands(text);
}

/** 글 속의 날짜(202610·20261001)를 '26.10월·'26.10.1. 로 — 조회 조건이 출처 줄에 그대로 찍혔다. */
function datesIn(text: string): string {
  return text.replace(/(^|[^\d])((?:19|20)\d{4}(?:\d{2})?)(?!\d)/g, (_, before: string, digits: string) => `${before}${shortDate(digits)}`);
}

function htmlTable(table: TableData, limit?: number): string {
  const rows = limit === undefined ? table.rows : table.rows.slice(0, limit);
  const right = table.columns.map((_, index) => numeric(index, rows));
  const cell = (tag: string, value: unknown, index: number) => (
    `<${tag}${right[index] ? ' class="num"' : ''}>${escape(tag === 'th' ? String(value ?? '') : cellText(value))}</${tag}>`);
  const head = `<thead><tr>${table.columns.map((column, index) => cell('th', column, index)).join('')}</tr></thead>`;
  const body = rows.map((row) => `<tr>${row.map((value, index) => cell('td', value, index)).join('')}</tr>`).join('');
  return `<table>${head}<tbody>${body}</tbody></table>`;
}

/** 핵심 숫자. 카드가 아니라 한 줄 — 표가 없는 보고서는 이것이 숫자의 전부다(검토, 2026-10-23). */
/**
 * 숫자 자리에 올릴 핵심 숫자. PDF·워드가 함께 쓴다.
 * 짧은 값만 — 「안내 이번 달 계획이 0건이면 …」 같은 문장이 굵게 찍혔다(실제 테넌트, 2026-10-23).
 */
export function reportFigures(kpis: ReportDocument['kpis']): ReportDocument['kpis'] {
  return kpis.filter((kpi) => kpi.value.length <= 16 && !/\s/.test(kpi.value.trim()))
    .map((kpi) => ({
      label: kpi.label,
      // 기간 값(202610)은 수가 아니라 기간이다 — 「202,610」으로 찍혔다(실제 테넌트, 2026-10-23).
      value: shortDate(kpi.value.trim()) !== kpi.value.trim() ? shortDate(kpi.value.trim())
        : /^-?\d+(?:\.\d+)?$/.test(kpi.value.trim()) ? shown(Number(kpi.value)) : kpi.value,
    }));
}

function figures(report: ReportDocument): string {
  const shownKpis = reportFigures(report.kpis);
  if (!shownKpis.length) return '';
  return `<p class="figures">${shownKpis.map((kpi) => (
    `<span>${kpi.label ? `${escape(kpi.label)} ` : ''}<b>${escape(kpi.value)}</b></span>`)).join('')}</p>`;
}

function detailSection(report: ReportDocument, fallback?: TableData): string {
  const table = report.table ?? fallback;
  if (!table || !table.rows.length || report.detailRows === 0) return '';
  const cut = table.truncated ? `<p class="note">${table.truncated}행이 용량 제한으로 잘렸습니다.</p>` : '';
  return `<section><h2>${escape(report.detailHeading ?? '상세 내역')}</h2>${htmlTable(table)}${cut}</section>`;
}

/** 〈그림 n〉 제목 (단위: 건) */
export function figureCaption(chart: ReportChart, number: number): string {
  return `〈그림 ${number}〉 ${chart.title}${chart.unit ? ` (단위: ${chart.unit})` : ''}`;
}

/** 〈표 n〉 예외 행 제목 — 다섯보다 많으면 「외 N건」. */
export function exceptionCaption(exceptions: NonNullable<ReportDocument['exceptions']>, number: number): string {
  // 이름별로 묶인 표면 실은 건수의 합으로 센다 — 줄 수가 아니다.
  const more = exceptions.total - (exceptions.listed ?? exceptions.table.rows.length);
  return `〈표 ${number}〉 ${exceptions.title} (${more > 0 ? `외 ${shown(more)}건, ` : ''}단위: ${exceptions.unit ?? '건'})`;
}

export const STATUS_CAPTION = '처리 현황 (단위: 건, %)';

/**
 * 칸 제목. 공지는 □ 안내 사항, □ 요청 사항이다(확인 필요 사항은 없다). PDF·워드·발표자료·채팅 답이 함께 쓴다. 공지판에 붙일
 * 문서가 □ 요약, 「…판단 불가」로 나갔다(실측 2026-10-26).
 */
export function sectionTitles(report: Pick<ReportDocument, 'genre'>): { summary: string; checks: string; actions: string } {
  return report.genre === 'notice'
    ? { summary: '안내 사항', checks: '참고 사항', actions: '요청 사항' }
    : { summary: '요약', checks: '확인 필요 사항', actions: '향후 조치(안)' };
}

/** 목록 표 중 그 자리의 것. 묶음별 현황은 그림 앞, 상위 명단은 그림 뒤. */
export function tablesAt(report: ReportDocument, role: 'groups' | 'top'): NonNullable<ReportDocument['tables']> {
  return (report.tables ?? []).filter((one) => one.role === role);
}

/** 공지의 받는 사람. 사내 공지판에 붙이는 문서다. 임원도 받는다(사용자 확인 2026-10-27: 임원도 연차 촉진 대상). */
export const NOTICE_TO = '수신: 전 임직원';

/**
 * 발신 부서를 모를 때의 자리표시. 발신과 문의처는 로그인한 사람의 부서로 채운다(사용자 확인, 2026-10-27). 그 부서가 비었을 때만
 * 이것이고, 사용자가 워드에서 채운다.
 */
export const NOTICE_SENDER = '(담당 부서)';

/**
 * 공지의 번호 항목: 앞 항목(목적·대상), 뒤 항목(할 일과 기한), 끝에 문의처. PDF·워드·발표자료·채팅 답이 함께 쓴다.
 * 공지가 내부 보고 골격(□ 안내 사항, □ 요청 사항)으로 나가 반려됐다(국가직 팀장 검토, 연차 공지). 문의처는 문서가 붙이므로
 * 모델이 쓴 문의처·발신 줄은 뺀다. 부서가 넘어오지 않으면 자리표시다.
 */
export function noticeItems(report: Pick<ReportDocument, 'points' | 'summary' | 'unit'>): string[] {
  const lines = report.points ? [...report.points.summary, ...report.points.actions]
    : report.summary.split('\n').map((line) => line.trim()).filter(Boolean);
  const own = lines.filter((line) => !/^(?:문의처?|발신|연락처)\s*[:：]/.test(line));
  return [...own, `문의처: ${report.unit?.trim() || NOTICE_SENDER}`].map((line, index) => `${index + 1}. ${line}`);
}

/** 공지의 게시일(「'26. 10. 26.」). 기준일에서 시각을 뗀 것이다. 조회 시각은 게시물에 필요 없다. */
export function noticeDate(report: Pick<ReportDocument, 'basis'>): string {
  return report.basis?.match(/'\d{2}\.\s*\d{1,2}\.\s*\d{1,2}\./)?.[0] ?? '';
}

/** 공지의 〈참고〉 표: 부서별 현황에 기준일을 붙인다(「('26. 10. 26. 기준, 단위: 명, 일)」). 여럿이면 〈참고 1〉, 〈참고 2〉. */
export function noticeTables(report: ReportDocument): { caption: string; table: TableData }[] {
  const date = noticeDate(report);
  const list = [...tablesAt(report, 'groups'), ...tablesAt(report, 'top')];
  return list.map((one, index) => {
    const dated = !date ? one.caption : one.caption.indexOf('(') >= 0
      ? one.caption.replace('(', `(${date} 기준, `) : `${one.caption} (${date} 기준)`;
    return { caption: `〈참고${list.length > 1 ? ` ${index + 1}` : ''}〉 ${dated}`, table: one.table };
  });
}

/** 공지 끝의 게시일과 발신 부서(오른쪽). 부서를 모르면 자리표시다. */
export function noticeSign(report: Pick<ReportDocument, 'basis' | 'unit'>): string[] {
  return [noticeDate(report), report.unit?.trim() || NOTICE_SENDER].filter(Boolean);
}

/** 제목 아래 줄 — 「('26. 10. 23.(금) 기준) · 유지보수팀 홍길동」. PDF·워드·발표자료가 함께 쓴다. */
export function reportMeta(report: ReportDocument): string {
  return [report.basis ? `(${report.basis})` : '', report.author ?? ''].filter(Boolean).join(' · ');
}

/**
 * 줄 바로 아래 * 줄(갈래 숫자를 센 칸)을 끼운 목록 — 문서 끝 ※ 묶음에서는 어느 줄의 근거인지 안 보였다(8차 검토).
 * 이어진 줄의 각주가 같으면 마지막 줄 아래에 한 번. 「* 」로 시작하는 것이 각주다. PDF·워드·발표자료가 함께 쓴다.
 */
export function notedItems(list: string[], report: ReportDocument): string[] {
  const noteOf = (line: string) => (report.lineNotes ?? []).find((one) => one.line === line)?.note;
  return list.flatMap((item, index) => {
    const note = noteOf(item);
    return note && noteOf(list[index + 1] ?? '') !== note ? [item, `* ${note}`] : [item];
  });
}

/** 문서 끝 ※ 줄 — 자료(조회 화면·기간)와 산식. 조회 조건의 날짜는 '26.10월 로. */
export function reportNotes(report: ReportDocument): string[] {
  const rate = report.statusTable?.columns[report.statusTable.columns.length - 1]?.replace(/\(%\)$/, '').trim();
  const watched = rate?.replace(/\s*비율$/, '');
  const looked = report.basis ? `, ${report.basis.replace(/\s*기준$/, '')} 조회` : '';
  const months = (report.statusTable?.rows ?? []).map((row) => String(row[0] ?? '').match(/^'\d{2}\.\d{1,2}월/)?.[0])
    .filter((month): month is string => !!month);
  return [
    report.sources.length ? `※ 자료: ${mergedSources(report.sources, report.title, months.length > 1 ? months : undefined)}${looked}` : '',
    // 원자료 자체의 결함(없는 날짜 등) — ERP 에서 고칠 일이라 자료 줄 바로 아래에(10차 검토).
    ...(report.dataNotes ?? []).map((note) => `※ ${note}`),
    ...(report.axisNotes ?? []).map((note) => `※ ${note}`),
    // 산식은 말로 푼다. 나눗셈 기호 같은 수식기호는 산문에 쓰지 않는다(사용자 전역 지침 stop-slop-ko, 11차 검토 때 스캐너 경고).
    rate && watched ? `※ ${rate}: ${watched} 건수를 전체 건수로 나눈 백분율` : '',
    report.statusRule ? `※ 판정: ${report.statusRule}` : '',
  ].filter(Boolean);
}

/**
 * 같은 화면의 조회를 한 줄로 — 「점검결과등록('26.8월·'26.9월·'26.10월분)」. 조회 조건 모양(「기간 …, 결과 전체」)은
 * 문서의 말이 아니다(국가직 팀장 2차 검토, 2026-10-23).
 */
/** 「'26.9월」·「'26.10.1.」의 차례 값 — 260900, 261001. */
function dateOrder(date: string): number {
  const found = date.match(/^'(\d{2})\.(\d{1,2})(?:\.(\d{1,2})\.)?/);
  return found ? Number(found[1]) * 10000 + Number(found[2]) * 100 + Number(found[3] ?? 0) : 0;
}

function mergedSources(sources: string[], title = '', months?: string[]): string {
  // 제목이 한 달(「10월 …」)을 말하면 그 달의 조회만 — 쓰지 않은 8·9월이 출처에 남았다(3차 검토).
  // 숫자에 여러 달을 썼으면(〈표 1〉 '26.9월·'26.10월) 그 달들을 다 — 9월 숫자를 쓰고 10월분만 적었다(10차 검토).
  const month = months ? undefined : title.match(/(\d{1,2})월/)?.[1];
  const inMonths = (date: string) => (months ?? []).some((one) => date === one || date.startsWith(`${one.replace(/월$/, '')}.`));
  const periods = new Map<string, string[]>();
  sources.forEach((source) => {
    const found = source.match(/^(.*?)\s*\((.*)\)\s*$/);
    const name = (found ? found[1] : source)?.trim() ?? source;
    const dates = (datesIn(found?.[2] ?? '').match(/'\d{2}\.\d{1,2}(?:월|\.\d{1,2}\.)/g) ?? []);
    const list = periods.get(name) ?? [];
    dates.forEach((date) => { if (list.indexOf(date) < 0) list.push(date); });
    periods.set(name, list);
  });
  return Array.from(periods.entries())
    .map(([name, all]) => {
      const used = months ? all.filter(inMonths)
        : month ? all.filter((date) => new RegExp(`^'\\d{2}\\.${month}(?:월|\\.)`).test(date)) : [];
      // 달은 차례대로 — 10월을 먼저 조회한 판이 「'26.10월·'26.9월분」으로 적었다(2026-10-25).
      const dates = (used.length ? used : all).slice().sort((left, right) => dateOrder(left) - dateOrder(right));
      return dates.length ? `${name}(${dates.join('·')}분)` : name;
    }).join(', ');
}

/**
 * 공문서 골격(국가직 팀장 검토, 2026-10-23). 제목(명사형) → (기준일) · 작성 부서 → □ 요약 → □ 현황(표·그림)
 * → □ 확인 필요 사항 → □ 향후 조치(안) → ※ 자료·산식. 원자료는 「전체 목록」을 청했을 때만 별첨으로.
 */
function officialBody(report: ReportDocument, fallback?: TableData): string {
  if (report.genre === 'notice') return noticeBody(report);
  let tables = 0;
  const caption = (text: string) => `<p class="caption">${escape(text)}</p>`;
  const items = (list: string[]) => notedItems(list, report)
    .map((item) => (item.startsWith('* ') ? caption(item) : `<p class="o">○ ${escape(item)}</p>`)).join('');
  const section = (title: string, body: string) => (body ? `<h2>□ ${title}</h2>${body}` : '');
  const summary = report.points ? items(report.points.summary) : paragraphs(report.summary);
  // 〈표 1〉 아래 * 줄은 경과 외 미실시(5차 검토).
  const status = report.statusTable ? caption(`〈표 ${++tables}〉 ${STATUS_CAPTION}`) + htmlTable(report.statusTable)
    + (report.statusNote ? caption(`* ${report.statusNote}`) : '') : '';
  // 경과 표는 사실 자료다 — □ 현황에 둔다(3차 검토: 확인 필요 사항 아래에 있었다). 쏠림 줄은 그 표 위에(5차 검토).
  const exceptions = report.exceptions
    ? items(report.exceptions.points ?? []) + caption(exceptionCaption(report.exceptions, ++tables))
      + htmlTable(report.exceptions.table) : '';
  // 목록 표(부서별 현황은 그림 앞, 상위 명단은 그림 뒤). 표가 있으면 숫자 줄은 싣지 않는다. 표의 「계」와 같은 수다.
  const listed = (role: 'groups' | 'top') => tablesAt(report, role)
    .map((one) => caption(`〈표 ${++tables}〉 ${one.caption}`) + htmlTable(one.table)).join('');
  const groups = listed('groups');
  const charts = (report.charts ?? []).map((chart, index) => (
    `<section class="chart">${chart.message ? items([chart.message]) : ''}${caption(figureCaption(chart, index + 1))}`
    + `${chart.series.length > 1 ? legendOf(chart) : ''}${chartSvg(chart)}</section>`)).join('');
  const top = listed('top');
  const table = report.table ?? fallback;
  const appendix = report.detailRows === undefined && table?.rows.length
    ? `<h2>〈별첨〉 전체 목록</h2>${htmlTable(table)}` : '';
  const notes = reportNotes(report).map((line) => escape(line)).join('<br>');
  const meta = reportMeta(report);
  const titles = sectionTitles(report);
  return `<h1>${escape(report.title)}</h1><p class="meta">${escape(meta)}</p>`
    + section(titles.summary, summary)
    + section('현황', (status || (exceptions || report.tables?.length ? '' : figures(report))) + exceptions + groups + charts + top)
    + section(titles.checks, report.points ? items(report.points.checks) : '')
    + section(titles.actions, report.points ? items(report.points.actions) : '')
    + appendix
    + (notes ? `<footer>${notes}</footer>` : '');
}

/**
 * 공지(사내 게시용 안내문)는 게시물 꼴이다: 제목 → 수신 → 번호 항목 → 〈참고〉 표·그림 → 게시일, 발신 부서(국가직 팀장 검토,
 * 연차 공지 반려). 머리의 조회 시각과 끝의 ※ 자료는 싣지 않는다.
 */
function noticeBody(report: ReportDocument): string {
  const caption = (text: string) => `<p class="caption">${escape(text)}</p>`;
  const items = noticeItems(report).map((item) => `<p class="n">${escape(item)}</p>`).join('');
  const tables = noticeTables(report).map((one) => caption(one.caption) + htmlTable(one.table)).join('');
  const charts = (report.charts ?? []).map((chart, index) => (
    `<section class="chart">${chart.message ? `<p class="o">○ ${escape(chart.message)}</p>` : ''}${caption(figureCaption(chart, index + 1))}`
    + `${chart.series.length > 1 ? legendOf(chart) : ''}${chartSvg(chart)}</section>`)).join('');
  const sign = noticeSign(report);
  return `<h1>${escape(report.title)}</h1><p class="meta">${escape(NOTICE_TO)}</p>${items}${tables}${charts}`
    + (sign.length ? `<p class="sign">${sign.map(escape).join('<br>')}</p>` : '');
}

function legendOf(chart: ReportChart): string {
  return `<div class="legend">${chart.series.map((series) => (
    `<span><i style="background:${colorOf(chart, series.name)}"></i>${escape(series.name)}</span>`)).join('')}</div>`;
}

function paragraphs(text: string): string {
  return text.split(/\n\s*\n/).filter((block) => block.trim())
    .map((block) => `<p class="summary">${escape(block.trim()).replace(/\n/g, '<br>')}</p>`).join('');
}

const STYLE = '@page{size:A4;margin:12mm}*{box-sizing:border-box}'
  + `body{font-family:"맑은 고딕","Malgun Gothic",sans-serif;color:${INK};font-size:10.5pt;line-height:1.5;margin:0;background:#fff}`
  + 'h1{font-size:18pt;line-height:1.3;margin:0 0 4px;font-weight:700}'
  + `.meta{color:${SECONDARY};font-size:9pt;margin:0 0 8px}`
  + '.summary{font-size:11pt;margin:0 0 8px}'
  + '.o{font-size:10.5pt;margin:0 0 1px;padding-left:1.1em;text-indent:-1.1em}'
  + '.n{font-size:11pt;margin:0 0 6px;padding-left:1.4em;text-indent:-1.4em;word-break:keep-all}'
  + '.sign{text-align:right;font-size:11pt;margin:18px 0 0}'
  + `.next{font-size:11pt;margin:0 0 8px}.next b{margin-right:6px}.caption{color:${SECONDARY};font-size:9pt;margin:6px 0 3px}`
  + `.figures{margin:0 0 10px;color:${SECONDARY}}.figures span{margin-right:18px}.figures b{color:${INK};font-size:13pt;margin-left:4px}`
  + 'h2{font-size:10.5pt;font-weight:600;margin:8px 0 2px}'
  + '.chart{break-inside:avoid}.chart svg{display:block;font-size:11px}'
  + `.legend{font-size:9pt;color:${SECONDARY};margin:0 0 4px}.legend span{margin-right:14px}`
  + '.legend i{display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:5px;vertical-align:-1px}'
  + 'table{width:100%;border-collapse:collapse;font-size:9pt;line-height:1.35}thead{display:table-header-group}tr{break-inside:avoid}'
  + `th{text-align:left;font-weight:600;border-bottom:1.5px solid ${INK};padding:2px 6px}`
  + `td{border-bottom:1px solid ${HAIRLINE};padding:1px 6px}`
  + '.num{text-align:right;font-variant-numeric:tabular-nums}'
  + `.note{color:${SECONDARY};font-size:9pt;margin:6px 0 0}`
  + `footer{margin-top:6px;padding-top:4px;border-top:1px solid ${HAIRLINE};color:${MUTED};font-size:8.5pt;line-height:1.35}`;

/**
 * 한 장에서 조금(12% 이내) 넘치면 인쇄 전에 줄인다 — 줄 길이가 판마다 달라 여러 달 판이 26px 넘쳐 두 쪽이 됐다
 * (2026-10-25 09:14 다시 그리기). 앱은 300px 숨은 틀에 쓰고 바로 인쇄하므로 인쇄 폭(A4 − 여백 = 186mm)에서 잰다.
 * 줄일 때는 폭을 186mm 로 둔다 — 폭이 풀리면 줄인 만큼 넓어진 칸에 맞춰 그림이 커져 높이가 그대로였다.
 * 끝에 6px 를 남긴다 — 딱 맞춘 판(1,032px)은 인쇄 반올림에 두 쪽이 될 수 있다. 더 넘치는 문서(전체 목록)는 그대로 여러 쪽이다.
 */
const FIT = '(function(){var body=document.body,limit=273*96/25.4-6;body.style.width="186mm";var height=body.scrollHeight;'
  + 'if(height>limit&&height<=limit*1.12)body.style.zoom=String(Math.floor(limit/height*1000)/1000);else body.style.width="";})();';

function page(title: string, body: string): string {
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>${escape(title.slice(0, 60))}</title>`
    + `<style>${STYLE}</style></head><body>${body}<script>${FIT}</script></body></html>`;
}

export function reportHtml(
  report: ReportDocument | undefined,
  fallback: { answer: string; question: string; table?: TableData },
): string {
  if (!report) {
    const cut = fallback.table?.truncated ? `<p class="note">${fallback.table.truncated}행이 용량 제한으로 잘렸습니다.</p>` : '';
    return page(fallback.question, `<h1>${escape(fallback.question)}</h1>${paragraphs(fallback.answer)}`
      + `${cut}${fallback.table ? htmlTable(fallback.table) : ''}`);
  }
  if (report.basis) return page(report.title, officialBody(report, fallback.table));
  const heading = report.headline ?? report.title;
  const meta = [
    report.period,
    report.audience ? `보고 대상 ${report.audience}` : '',
    report.sources.length ? report.sources.join(', ') : '',
    new Date(report.generatedAt).toLocaleDateString('ko-KR'),
  ].filter(Boolean).map(escape).join(' · ');
  const body = `<h1>${escape(heading)}</h1><p class="meta">${meta}</p>`
    + figures(report)
    + paragraphs(report.summary)
    + (report.charts ?? []).map(chartSection).join('')
    + detailSection(report, fallback.table)
    + `<footer>산출 기준: ${escape(report.methodology)}</footer>`;
  return page(heading, body);
}
